import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  Users,
  Settings,
  LogOut,
  Send,
  SlidersHorizontal,
  Construction,
  ArrowLeft,
  Plus,
  Download,
  CheckCircle2,
  XCircle,
  MinusCircle,
  Loader2,
  AlertCircle,
  ChevronDown,
  CloudUpload,
  Info,
  Play,
  CircleX,
  Minus,
  X,
  Building2,
  Earth,
  Tag,
  ShieldCheck,
  ChartColumn,
  ExternalLink,
  Square,
  Clock,
  Search,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getPipelineCsvFormats,
  getPipelineStepDefinitions,
  uploadPipelineCsv,
  startPipelineRun,
  getPipelineRunStatus,
  listSearchDomains,
  listPipelineRuns,
  downloadPipelineInput,
  downloadPipelineResult,
  stopPipelineRun,
  type DomainCsvFormat,
  type CsvUploadResult,
  type StepDefinition,
  type SearchDomain,
  type PipelineRunSummary,
} from '../services/api';
import { CandidatesSection as CandidatesSectionNew, type Tab as CandidatesTab } from './admin/CandidatesSection';
import { BatchesSection } from './admin/BatchesSection';
import { TenantsSection } from './admin/TenantsSection';
import { SearchDomainsSection } from './admin/SearchDomainsSection';
import { AccessManagementSection } from './admin/AccessManagementSection';
import { PromptLabSection } from './admin/PromptLabSection';
import { SkillsSection } from './admin/SkillsSection';
import { LinkHealthSection } from './admin/LinkHealthSection';
import { SearchSessionsTab } from './admin/SearchSessionsTab';
import { PIPELINE_STEPS, deriveStepStatuses as deriveStepStatusesNew, type PipelineStep, type StepStatus } from './admin/constants';
import { SortFilterHeader } from './admin/SharedTableComponents';
import { FlaskConical, Activity } from 'lucide-react';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { AdminDataProvider } from './admin/AdminDataContext';

type AdminSection = 'ingestion' | 'candidates' | 'batches' | 'access' | 'configurator' | 'accounts' | 'search-domains' | 'prompt-lab' | 'skills' | 'link-health';

type SidebarItem = { id: AdminSection; slug: string; label: string; icon: React.ElementType };
type SidebarGroup = { label: string; items: SidebarItem[] };

const SIDEBAR_GROUPS: SidebarGroup[] = [
  {
    label: 'Pipeline',
    items: [
      { id: 'ingestion', slug: 'data-ingestion', label: 'Data Ingestion', icon: CloudUpload },
      { id: 'candidates', slug: 'candidates', label: 'Candidates', icon: Users },
      { id: 'batches', slug: 'batches', label: 'Batches', icon: Send },
    ],
  },
  {
    label: 'Account Management',
    items: [
      { id: 'accounts', slug: 'accounts-management', label: 'Accounts', icon: Building2 },
    ],
  },
  {
    label: 'Talent Model',
    items: [
      { id: 'search-domains', slug: 'managed-domains', label: 'Domains', icon: Earth },
      { id: 'skills', slug: 'skills', label: 'Skills', icon: Tag },
      { id: 'configurator', slug: 'profile-configurator', label: 'Profile Schemas', icon: SlidersHorizontal },
    ],
  },
  {
    label: 'Platform Administration',
    items: [
      { id: 'access', slug: 'access-management', label: 'Access Management', icon: ShieldCheck },
      { id: 'link-health', slug: 'health-check', label: 'Health Check', icon: Activity },
    ],
  },
];

const SIDEBAR_ITEMS = SIDEBAR_GROUPS.flatMap(g => g.items);

const SLUG_TO_SECTION: Record<string, AdminSection> = Object.fromEntries(
  [...SIDEBAR_ITEMS, { id: 'prompt-lab' as AdminSection, slug: 'prompt-lab' }].map(i => [i.slug, i.id])
);
const SECTION_TO_SLUG = Object.fromEntries(
  [...SIDEBAR_ITEMS, { id: 'prompt-lab' as AdminSection, slug: 'prompt-lab' }].map(i => [i.id, i.slug])
) as Record<AdminSection, string>;

// Pipeline steps and step status derivation imported from ./admin/constants.ts

function SectionPlaceholder({ title, description }: { title: string; description: string }) {
  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{title}</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">{description}</p>
      </div>
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-16 text-center">
        <Construction className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">Work in progress</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">This section is being built. Check back soon.</p>
      </div>
    </div>
  );
}

const PAGE_META: Record<AdminSection, { title: string; description: string }> = {
  ingestion: { title: 'Data Ingestion', description: 'Upload candidate CSVs and review past enrichment runs.' },
  candidates: { title: 'Candidates', description: 'Manage all candidates in the system.' },
  batches: { title: 'Batches', description: 'Manage and send candidate batches to companies.' },
  access: { title: 'Access Management', description: 'Manage companies and their users who have access to the platform.' },
  configurator: { title: 'Profile Schemas', description: 'Customize the order and visibility of sections in the candidate profile view for each domain.' },
  accounts: { title: 'Accounts', description: 'Manage whitelisted domains and their search configuration.' },
  'search-domains': { title: 'Domains', description: 'Manage talent pool categories available for search and ingestion.' },
  'prompt-lab': { title: 'Prompt Lab', description: 'Test and compare query parsing prompt variants.' },
  'skills': { title: 'Skills', description: 'Curate top skills shown to customers per tenant and domain.' },
  'link-health': { title: 'Health Check', description: 'Monitor link validity and candidate coverage.' },
};

function downloadSampleCsv(format: DomainCsvFormat) {
  const headers = format.columns.map((c) => c.name);
  const sampleRow = format.columns.map((c) => {
    if (c.name.toLowerCase().includes('id')) return '12345';
    if (c.name.toLowerCase().includes('resume')) return 'https://example.com/resume.pdf';
    return '';
  });
  const csv = [headers.join(','), sampleRow.join(',')].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = format.filename;
  a.click();
  URL.revokeObjectURL(url);
}

function estimatePipelineTime(rowCount: number): string {
  const minMinutes = Math.max(2, Math.ceil(rowCount * 2));
  const maxMinutes = Math.max(5, Math.ceil(rowCount * 5));
  if (maxMinutes <= 5) return '~2–5 min';
  return `~${minMinutes}–${maxMinutes} min`;
}

// ─── Numbered Step Header ──────────────────────────────────────────────────
function StepHeader({ number, title }: { number: number; title: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-semibold">
        {number}
      </div>
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
    </div>
  );
}

// ─── Add Candidates (Profile Enrichment) Page ──────────────────────────────
function AddCandidatesPage({ onBack, onComplete }: { onBack: () => void; onComplete: (groupName: string) => void }) {
  const [domains, setDomains] = useState<SearchDomain[]>([]);
  const [csvFormats, setCsvFormats] = useState<DomainCsvFormat[]>([]);
  const [stepDefs, setStepDefs] = useState<StepDefinition[]>([]);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [uploadGroup, setUploadGroup] = useState('');
  const [uploadResult, setUploadResult] = useState<CsvUploadResult | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pipeline state
  const [isRunning, setIsRunning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [stepStatuses, setStepStatuses] = useState<Record<string, StepStatus>>({});
  const [pipelineError, setPipelineError] = useState('');
  const [wasQueued, setWasQueued] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState<{ cached: number; new: number; total: number } | null>(null);
  const [expandedPrompt, setExpandedPrompt] = useState<string | null>(null);
  const [skipExisting, setSkipExisting] = useState(true);
  const [showExistingModal, setShowExistingModal] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'search' | 'upload'>('search');

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    Promise.all([listSearchDomains(), getPipelineCsvFormats(), getPipelineStepDefinitions()])
      .then(([d, f, s]) => {
        setDomains(d.filter((x) => x.isActive));
        setCsvFormats(f);
        setStepDefs(s);
        if (d.length > 0) setSelectedDomain(d[0].key);
      })
      .catch(() => {});
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  const domainLower = selectedDomain.toLowerCase();
  const selectedFormat: DomainCsvFormat = csvFormats.find((f) => f.domain.toLowerCase() === domainLower) ?? {
    domain: domainLower,
    label: domains.find((d) => d.key.toLowerCase() === domainLower)?.label ?? selectedDomain,
    filename: 'raw.csv',
    columns: [
      { name: 'Talent ID', required: true, description: 'Unique talent identifier (numeric)' },
      { name: 'Resume', required: false, description: 'URL to resume PDF (optional — falls back to DB resume data)' },
      { name: 'Primary Discipline', required: false, description: 'Sub-field within the domain (e.g., PCB Design, Quantum information)' },
      { name: 'Hourly Rate', required: false, description: 'Hourly rate in USD (numeric)' },
    ],
  };
  const pipelineStarted = isRunning || isComplete || isFailed;
  const uniqueCount = uploadResult?.uniqueTalentCount ?? uploadResult?.rowCount ?? 0;
  const allExist = !!(uploadResult?.existingTalents && uploadResult.existingTalents.length >= uniqueCount);
  const effectiveSkip = allExist ? false : skipExisting;
  const canRun = uploadFile && uploadResult && uploadGroup.trim() && !pipelineStarted && !(allExist && effectiveSkip);

  const STEP_TO_DEFS: Record<string, string[]> = {
    'data-preparation': ['csv-to-json', 'build-index'],
    'linkedin-validation': ['linkedin-validation'],
    'single-agent-local-resume-check': ['fraud-analysis'],
    'multi-agent-web-verification': ['profile-verification', 'scholar-validation'],
    'final-consolidation': ['profile-overview', 'final-report'],
  };

  const getPromptsForStep = (step: PipelineStep) => {
    const prompts: { name: string; role: string; text: string }[] = [];
    const defIds = STEP_TO_DEFS[step.id] || [];
    for (const defId of defIds) {
      const def = stepDefs.find((s) => s.id === defId);
      if (def?.prompts) {
        for (const p of def.prompts) {
          if (p.domain && p.domain.toLowerCase() !== domainLower) continue;
          if (p.text) prompts.push({ name: p.name, role: p.role, text: p.text });
        }
      }
    }
    return prompts;
  };

  const handleFile = useCallback(async (file: File) => {
    if (!selectedDomain) return;
    setUploadError('');
    setUploadResult(null);
    setUploadFile(null);
    setIsUploading(true);
    try {
      const result = await uploadPipelineCsv(selectedDomain.toLowerCase(), file);
      if (!result.valid) {
        setUploadError(result.errors?.join('\n') || 'The file could not be processed. Please check the format and try again.');
      } else {
        setUploadResult(result);
        setUploadFile(file);
      }
    } catch (err: any) {
      setUploadError(err?.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  }, [selectedDomain]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const startPolling = (runId: string) => {
    if (pollingRef.current) clearInterval(pollingRef.current);

    pollingRef.current = setInterval(async () => {
      try {
        const status = await getPipelineRunStatus(runId);
        const derived = deriveStepStatusesNew(status.currentStep, status.status, status.skippedSteps);
        setStepStatuses(derived);

        if (status.status === 'completed') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          setIsRunning(false);
          setIsComplete(true);
          // Backend auto-ingests on completion
        } else if (status.status === 'failed' || status.status === 'cancelled') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          setIsRunning(false);
          setIsFailed(true);
          setPipelineError('Enrichment failed. Please try again or contact support.');
        }
      } catch {
        // Network blip — keep polling
      }
    }, 3000);
  };

  const runningGuard = useRef(false);
  const handleRunEnrichment = async () => {
    if (!uploadFile || !selectedDomain || !uploadGroup.trim()) return;
    if (runningGuard.current) return;
    runningGuard.current = true;
    setIsRunning(true);
    setIsFailed(false);
    setIsComplete(false);
    setPipelineError('');
    setDuplicateInfo(null);

    const initialStatuses: Record<string, StepStatus> = {};
    PIPELINE_STEPS.forEach((s) => { initialStatuses[s.id] = 'pending'; });
    setStepStatuses(initialStatuses);

    try {
      const domain = selectedDomain.toLowerCase();
      const skipIds = effectiveSkip && uploadResult?.existingTalents?.length
        ? uploadResult.existingTalents.map((t) => t.talentId)
        : undefined;

      const result = await startPipelineRun({
        domains: [domain],
        forceFresh: !effectiveSkip,
        uploadGroup: uploadGroup.trim(),
        skipTalentIds: skipIds,
        file: uploadFile,
      });

      // All talents skipped — nothing to run
      if (result.skipped) {
        setIsRunning(false);
        setIsFailed(true);
        setPipelineError('No candidates were processed. Please re-upload the CSV and try again.');
        return;
      }

      if (result.status === 'queued') {
        setIsRunning(false);
        setIsComplete(true);
        setWasQueued(true);
        setPipelineError('');
        return;
      }

      if (result.cachedTalents > 0) {
        setDuplicateInfo({ cached: result.cachedTalents, new: result.newTalents, total: result.totalTalents });
      }

      if (result.newTalents === 0 && result.cachedTalents > 0) {
        const allDone: Record<string, StepStatus> = {};
        PIPELINE_STEPS.forEach((s) => { allDone[s.id] = 'completed'; });
        setStepStatuses(allDone);
        setIsRunning(false);
        setIsComplete(true);
        return;
      }

      startPolling(result.runId);
    } catch (err: any) {
      setIsRunning(false);
      setIsFailed(true);
      const msg = err?.response?.data?.message;
      setPipelineError(msg || 'Something went wrong. Please try again or contact support.');
    }
  };

  const handleReset = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    runningGuard.current = false;
    setUploadResult(null);
    setUploadFile(null);
    setUploadError('');
    setIsRunning(false);
    setIsComplete(false);
    setIsFailed(false);
    setStepStatuses({});
    setPipelineError('');
    setDuplicateInfo(null);
    setUploadGroup('');
    setSkipExisting(true);
    setWasQueued(false);
  };

  const renderStepIcon = (stepId: string, idx: number) => {
    const status = stepStatuses[stepId];
    if (status === 'completed') return <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />;
    if (status === 'running') return <Loader2 className="w-5 h-5 text-blue-500 animate-spin flex-shrink-0" />;
    if (status === 'failed') return <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />;
    if (status === 'skipped') return <MinusCircle className="w-5 h-5 text-gray-400 flex-shrink-0" />;
    if (pipelineStarted) return <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600 flex-shrink-0" />;
    return <span className="text-sm font-medium text-gray-400 w-5 text-center flex-shrink-0">{idx + 1}</span>;
  };

  return (
    <div className="transition-opacity duration-300">
      {/* Back link */}
      <div className="px-8 pt-8 pb-4 max-w-5xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Data Ingestion
        </button>
      </div>

      <div className="px-8 pb-8 max-w-5xl mx-auto space-y-8">
        {/* Header: title + Search / Upload CSV tab toggle */}
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Add Candidates</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
              {activeTab === 'search'
                ? 'Continue from your recent Talent Search sessions.'
                : 'Upload a CSV when candidates are coming from outside Talent Search.'}
            </p>
          </div>
          <div className="inline-flex w-fit rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 p-1">
            <button
              onClick={() => setActiveTab('search')}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                activeTab === 'search'
                  ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Search className="h-4 w-4" />
              Search
            </button>
            <button
              onClick={() => setActiveTab('upload')}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                activeTab === 'upload'
                  ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <CloudUpload className="h-4 w-4" />
              Upload CSV
            </button>
          </div>
        </div>

        {activeTab === 'search' && <SearchSessionsTab onUploadCsv={() => setActiveTab('upload')} />}

        {activeTab === 'upload' && (
        <>
        {/* ─── Step 1: Select Domain ─── */}
        <div className="space-y-3">
          <StepHeader number={1} title="Select Domain" />
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Domain</label>
            <div className="relative">
              {domains.length === 0 ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading domains...
                </div>
              ) : (
                <>
                  <select
                    value={selectedDomain}
                    onChange={(e) => { setSelectedDomain(e.target.value); handleReset(); }}
                    disabled={pipelineStarted}
                    className="w-full appearance-none px-3 py-2 pr-10 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {domains.map((d) => (
                      <option key={d.key} value={d.key}>{d.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              The CSV format and enrichment steps will change based on the selected domain.
            </p>
          </div>
        </div>

        {/* ─── Step 2: Select Candidates ─── */}
        <div className="space-y-4">
          <StepHeader number={2} title="Select Candidates" />

          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
            {/* Upload CSV tab */}
            <div className="flex border-b border-gray-200 dark:border-gray-800">
              <button className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium border-b-2 border-blue-600 text-blue-600 bg-blue-50/30 dark:bg-blue-900/10">
                <CloudUpload className="w-4 h-4" />
                Upload CSV
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* CSV Format Info */}
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Expected CSV format</span>
                    <span className="px-2 py-0.5 rounded text-xs font-mono bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                      {selectedFormat.filename}
                    </span>
                  </div>
                  <button
                    onClick={() => downloadSampleCsv(selectedFormat)}
                    className="flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700"
                  >
                    <Download className="w-4 h-4" />
                    Download Sample CSV
                  </button>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400">
                      <th className="pb-2 font-medium">Column</th>
                      <th className="pb-2 font-medium">Required</th>
                      <th className="pb-2 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700 dark:text-gray-300">
                    {selectedFormat.columns.map((col) => (
                      <tr key={col.name} className="border-t border-gray-200 dark:border-gray-700">
                        <td className="py-2 font-mono text-sm">{col.name}</td>
                        <td className="py-2">
                          {col.required
                            ? <span className="text-xs font-medium text-red-600 dark:text-red-400">Required</span>
                            : <span className="text-xs font-medium text-gray-400">Optional</span>}
                        </td>
                        <td className="py-2 text-gray-500 dark:text-gray-400">{col.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Dropzone */}
              {!uploadResult && !pipelineStarted && (
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                    isDragging
                      ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-300 dark:border-gray-700 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFile(file); e.target.value = ''; }}
                    className="hidden"
                  />
                  {isUploading ? (
                    <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-2" />
                  ) : (
                    <CloudUpload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  )}
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {isUploading ? 'Uploading...' : 'Click or drop to upload CSV'}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Supports .csv files up to 10MB</p>
                </div>
              )}

              {/* Upload Error */}
              {uploadError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-red-700 dark:text-red-300 whitespace-pre-wrap">{uploadError}</div>
                  </div>
                </div>
              )}

              {/* Upload Success */}
              {uploadResult && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-green-800 dark:text-green-200">{uploadResult.filename}</p>
                        <p className="text-xs text-green-600 dark:text-green-400">
                          {uploadResult.rowCount} candidate{uploadResult.rowCount !== 1 ? 's' : ''} &middot; {uploadResult.headers.length} columns
                        </p>
                      </div>
                    </div>
                    {!pipelineStarted && (
                      <button onClick={() => { setUploadResult(null); setUploadFile(null); setUploadError(''); }} className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                        Change file
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Existing Talents Warning */}
              {uploadResult && !pipelineStarted && uploadResult.existingTalents && uploadResult.existingTalents.length > 0 && (() => {
                const uc = uploadResult.uniqueTalentCount ?? uploadResult.rowCount;
                const allExistLocal = uploadResult.existingTalents.length >= uc;
                const newCount = uc - uploadResult.existingTalents.length;
                return (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 space-y-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                          {allExistLocal
                            ? `All ${uc} candidate${uc !== 1 ? 's' : ''} have already been processed for this domain`
                            : `${uploadResult.existingTalents.length} of ${uc} unique candidate${uc !== 1 ? 's' : ''} already processed for this domain`}
                          {uploadResult.rowCount > uc && (
                            <span className="font-normal text-amber-600 dark:text-amber-400"> ({uploadResult.rowCount - uc} duplicate row{uploadResult.rowCount - uc !== 1 ? 's' : ''} ignored)</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="ml-6">
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-amber-700 dark:text-amber-300">
                        {uploadResult.existingTalents.slice(0, 5).map((t) => (
                          <span key={`${t.talentId}_${t.domain}`} className="inline-flex items-center gap-1.5">
                            {t.name && <span className="font-medium">{t.name}</span>}
                            <span className="font-mono">{t.talentId}</span>
                            <span className={`px-1 py-0.5 rounded text-[10px] font-medium ${t.status === 'accepted' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : t.status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                              {t.status.replace('_', ' ')}
                            </span>
                          </span>
                        ))}
                        {uploadResult.existingTalents.length > 5 && (
                          <button onClick={() => setShowExistingModal(true)} className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 underline cursor-pointer">
                            and {uploadResult.existingTalents.length - 5} more
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="ml-6 flex flex-col gap-2">
                      {!allExistLocal && (
                        <div
                          onClick={() => setSkipExisting(true)}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${skipExisting ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-600' : 'border-amber-200 dark:border-amber-700 hover:border-amber-300'}`}
                        >
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${skipExisting ? 'border-blue-600' : 'border-amber-400'}`}>
                            {skipExisting && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                          </div>
                          <div className="flex-1">
                            <span className={`text-sm font-medium ${skipExisting ? 'text-blue-800 dark:text-blue-300' : 'text-amber-800 dark:text-amber-200'}`}>Run only for {newCount} new candidate{newCount !== 1 ? 's' : ''}</span>
                            <p className={`text-xs mt-0.5 ${skipExisting ? 'text-blue-600 dark:text-blue-400' : 'text-amber-600 dark:text-amber-400'}`}>Previously processed candidates will be skipped</p>
                          </div>
                          {uploadResult.newTalents && uploadResult.newTalents.length > 0 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setShowNewModal(true); }}
                              className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 underline shrink-0"
                            >
                              View
                            </button>
                          )}
                        </div>
                      )}
                      <div
                        onClick={() => setSkipExisting(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${(allExistLocal || !skipExisting) ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-600' : 'border-amber-200 dark:border-amber-700 hover:border-amber-300'}`}
                      >
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${(allExistLocal || !skipExisting) ? 'border-blue-600' : 'border-amber-400'}`}>
                          {(allExistLocal || !skipExisting) && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                        </div>
                        <div className="flex-1">
                          <span className={`text-sm font-medium ${(allExistLocal || !skipExisting) ? 'text-blue-800 dark:text-blue-300' : 'text-amber-800 dark:text-amber-200'}`}>Re-run enrichment for all {uc} candidate{uc !== 1 ? 's' : ''}</span>
                          <p className={`text-xs mt-0.5 ${(allExistLocal || !skipExisting) ? 'text-blue-600 dark:text-blue-400' : 'text-amber-600 dark:text-amber-400'}`}>All candidates will be re-evaluated from scratch</p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowExistingModal(true); }}
                          className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 underline shrink-0"
                        >
                          View
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* ─── Step 3: Name this upload ─── */}
        <div className="space-y-3">
          <StepHeader number={3} title="Name this upload" />
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <label htmlFor="upload-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Upload group name
            </label>
            <input
              id="upload-name"
              type="text"
              value={uploadGroup}
              onChange={(e) => setUploadGroup(e.target.value)}
              disabled={pipelineStarted}
              placeholder="e.g. SWE Cohort — May 2026"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow disabled:opacity-50"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              This label will appear next to each candidate in the In Review tab so you can easily identify the group.
            </p>
          </div>
        </div>

        {/* ─── Step 4: Enrichment Steps ─── */}
        <div className="space-y-3">
          <StepHeader number={4} title="Enrichment Steps" />

          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
            {PIPELINE_STEPS.map((step, idx) => {
              const status = stepStatuses[step.id];
              const prompts = getPromptsForStep(step);
              const isExpanded = expandedPrompt === step.id;
              return (
                <div
                  key={step.id}
                  className={`transition-colors ${
                    status === 'running' ? 'bg-blue-50/50 dark:bg-blue-900/10' :
                    status === 'failed' ? 'bg-red-50/50 dark:bg-red-900/10' :
                    status === 'skipped' ? 'opacity-60' :
                    ''
                  }`}
                >
                  <div className="p-5">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">
                        {renderStepIcon(step.id, idx)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className={`font-semibold ${
                            status === 'running' ? 'text-blue-700 dark:text-blue-300' :
                            status === 'completed' ? 'text-green-700 dark:text-green-300' :
                            status === 'failed' ? 'text-red-700 dark:text-red-300' :
                            'text-gray-900 dark:text-gray-100'
                          }`}>
                            {step.label}
                          </h4>
                          {status === 'skipped' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                              Skipped · covered by Matching
                            </span>
                          )}
                          {step.hasAi && prompts.length > 0 && (
                            <button
                              onClick={() => setExpandedPrompt(isExpanded ? null : step.id)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors cursor-pointer"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                              {isExpanded ? 'Hide AI content' : 'View AI content'}
                            </button>
                          )}
                          {step.hasAi && prompts.length === 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800">
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                              AI content
                            </span>
                          )}
                        </div>
                        <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Prompt Viewer */}
                  {isExpanded && prompts.length > 0 && (
                    <div className="px-5 pb-5 pt-0 ml-8">
                      <div className="space-y-3">
                        {prompts.map((p, i) => (
                          <div key={i} className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                              <div className="flex items-center gap-2">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${
                                  p.role === 'system'
                                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                                    : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                }`}>
                                  {p.role}
                                </span>
                                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{p.name}</span>
                              </div>
                            </div>
                            <pre className="p-3 text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap max-h-60 overflow-y-auto font-mono leading-relaxed">
                              {p.text}
                            </pre>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Duplicate Info */}
          {duplicateInfo && duplicateInfo.cached > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <span className="font-medium">{duplicateInfo.cached} of {duplicateInfo.total}</span> already have cached results.
                Running enrichment for <span className="font-medium">{duplicateInfo.new}</span> new candidate{duplicateInfo.new !== 1 ? 's' : ''}.
              </p>
            </div>
          )}

          {/* Pipeline Error */}
          {isFailed && pipelineError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
              <p className="text-sm text-red-700 dark:text-red-300">{pipelineError}</p>
            </div>
          )}

          {/* Completion status */}
          {isComplete && (
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
              <div className="flex items-center justify-between">
                <div className={`flex items-center gap-2 text-sm ${wasQueued ? 'text-amber-700 dark:text-amber-300' : 'text-green-700 dark:text-green-300'}`}>
                  {wasQueued ? <Clock className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                  {wasQueued
                    ? 'Run queued — it will start automatically when the current pipeline finishes.'
                    : 'Enrichment complete — candidates are ready for review.'}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleReset}
                    className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    Add More
                  </button>
                  {wasQueued ? (
                    <button
                      onClick={onBack}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Back to Runs
                    </button>
                  ) : (
                    <button
                      onClick={() => onComplete(uploadGroup.trim())}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      View Results
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Estimated time + Run button */}
          {!pipelineStarted && (
            <>
              {uploadResult && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    Estimated time for {uploadResult.rowCount} profile{uploadResult.rowCount !== 1 ? 's' : ''}:{' '}
                    <span className="font-medium">{estimatePipelineTime(uploadResult.rowCount)}</span>
                  </p>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={handleRunEnrichment}
                  disabled={!canRun || isRunning}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {isRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  {isRunning ? 'Starting...' : 'Run Enrichment'}
                </button>
              </div>
            </>
          )}
        </div>
        </>
        )}
      </div>

      {/* Existing Talents Modal */}
      {showExistingModal && uploadResult?.existingTalents && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowExistingModal(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl w-full max-w-2xl mx-4 max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Already Processed Candidates</h3>
                <p className="text-xs text-gray-500 mt-0.5">{uploadResult.existingTalents.length} candidate{uploadResult.existingTalents.length !== 1 ? 's' : ''} found for this domain</p>
              </div>
              <button onClick={() => setShowExistingModal(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 overscroll-contain">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800/80 backdrop-blur-sm">
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <th className="px-5 py-2.5">Name</th>
                    <th className="px-5 py-2.5">Talent ID</th>
                    <th className="px-5 py-2.5">Domain</th>
                    <th className="px-5 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {uploadResult.existingTalents.map((t) => (
                    <tr key={`${t.talentId}_${t.domain}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-5 py-2.5 text-gray-900 dark:text-gray-100 font-medium">{t.name || '—'}</td>
                      <td className="px-5 py-2.5 font-mono text-gray-600 dark:text-gray-400">{t.talentId}</td>
                      <td className="px-5 py-2.5">
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                          {t.domain.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-5 py-2.5">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          t.status === 'accepted' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                          t.status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                          'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        }`}>
                          {t.status.replace('_', ' ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* New Candidates Modal */}
      {showNewModal && uploadResult?.newTalents && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowNewModal(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl w-full max-w-2xl mx-4 max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">New Candidates</h3>
                <p className="text-xs text-gray-500 mt-0.5">{uploadResult.newTalents.length} candidate{uploadResult.newTalents.length !== 1 ? 's' : ''} not yet processed</p>
              </div>
              <button onClick={() => setShowNewModal(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 overscroll-contain">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800/80 backdrop-blur-sm">
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <th className="px-5 py-2.5">Name</th>
                    <th className="px-5 py-2.5">Talent ID</th>
                    <th className="px-5 py-2.5">Domain</th>
                    <th className="px-5 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {uploadResult.newTalents.map((t) => (
                    <tr key={t.talentId} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-5 py-2.5 text-gray-900 dark:text-gray-100 font-medium">{t.name || '—'}</td>
                      <td className="px-5 py-2.5 font-mono text-gray-600 dark:text-gray-400">{t.talentId}</td>
                      <td className="px-5 py-2.5">
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                          {t.domain.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-5 py-2.5">
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">new</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

// ─── Run Progress Modal ──────────────────────────────────────────────────
function RunProgressModal({ run, onClose }: { run: PipelineRunSummary; onClose: () => void }) {
  const [status, setStatus] = React.useState(run);
  const [skippedSteps, setSkippedSteps] = React.useState<string[]>([]);
  const pollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  React.useEffect(() => {
    const poll = async () => {
      try {
        const fresh = await getPipelineRunStatus(run.id);
        setStatus((prev) => ({ ...prev, status: fresh.status, currentStep: fresh.currentStep, finishedAt: fresh.finishedAt }));
        setSkippedSteps(fresh.skippedSteps ?? []);
        if (['completed', 'failed', 'cancelled', 'rejected'].includes(fresh.status)) {
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch { /* ignore */ }
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [run.id]);

  const stepStatuses = deriveStepStatusesNew(status.currentStep, status.status, skippedSteps);
  const isActive = status.status === 'running' || status.status === 'pending';

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Pipeline Progress</h3>
            <p className="text-xs text-gray-500 mt-0.5 font-mono">{status.id}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-3 flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800">
          {status.domain && <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{status.domain.toUpperCase()}</span>}
          <span>{status.totalTalents} talent{status.totalTalents !== 1 ? 's' : ''}</span>
          {status.groupName && <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-800">{status.groupName}</span>}
          {isActive && <Loader2 className="w-4 h-4 text-blue-500 animate-spin ml-auto" />}
          {status.status === 'completed' && <CheckCircle2 className="w-4 h-4 text-green-500 ml-auto" />}
          {status.status === 'failed' && <XCircle className="w-4 h-4 text-red-500 ml-auto" />}
        </div>

        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {PIPELINE_STEPS.map((step) => {
            const s = stepStatuses[step.id];
            return (
              <div key={step.id} className={`px-6 py-4 ${s === 'running' ? 'bg-blue-50/50 dark:bg-blue-900/10' : s === 'failed' ? 'bg-red-50/50 dark:bg-red-900/10' : s === 'skipped' ? 'opacity-60' : ''}`}>
                <div className="flex items-center gap-3">
                  {s === 'completed' && <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />}
                  {s === 'running' && <Loader2 className="w-5 h-5 text-blue-500 animate-spin flex-shrink-0" />}
                  {s === 'failed' && <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />}
                  {s === 'skipped' && <MinusCircle className="w-5 h-5 text-gray-400 flex-shrink-0" />}
                  {s === 'pending' && <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600 flex-shrink-0" />}
                  <div>
                    <h4 className={`text-sm font-semibold ${s === 'running' ? 'text-blue-700 dark:text-blue-300' : s === 'completed' ? 'text-green-700 dark:text-green-300' : s === 'failed' ? 'text-red-700 dark:text-red-300' : 'text-gray-500 dark:text-gray-400'}`}>
                      {step.label}
                      {s === 'skipped' && <span className="ml-2 font-normal text-gray-400">· skipped (covered by Matching)</span>}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{step.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {status.status === 'failed' && status.error && (
          <div className="mx-6 mb-4 mt-2 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-xs text-red-700 dark:text-red-300 font-mono whitespace-pre-wrap">{status.error}</p>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ─── Run History Card ──────────────────────────────────────────────────────
function RunCard({ run, onClickRunning, onCancelled, onNavigateToCandidates }: { run: PipelineRunSummary; onClickRunning?: (run: PipelineRunSummary) => void; onCancelled?: () => void; onNavigateToCandidates?: (groupName: string, tab: CandidatesTab) => void }) {
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(run.status === 'queued' ? `Remove queued run ${run.id}?` : `Cancel run ${run.id}? This will kill the pipeline process.`)) return;
    setCancelling(true);
    try {
      await stopPipelineRun(run.id);
      onCancelled?.();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Failed to cancel run');
    } finally {
      setCancelling(false);
    }
  };

  const statusConfig =
    run.status === 'queued'
      ? { text: 'text-amber-700 dark:text-amber-400', bar: 'bg-amber-500', track: 'bg-amber-100 dark:bg-amber-900/30', icon: 'text-amber-500', label: 'Queued' }
    : run.status === 'running' || run.status === 'pending'
      ? { text: 'text-blue-700 dark:text-blue-400', bar: 'bg-blue-500', track: 'bg-blue-100 dark:bg-blue-900/30', icon: 'text-blue-500', label: 'Running' }
    : run.status === 'completed'
      ? { text: 'text-green-700 dark:text-green-400', bar: 'bg-green-500', track: 'bg-green-100 dark:bg-green-900/30', icon: 'text-green-500', label: 'Completed' }
    : run.status === 'failed' || run.status === 'rejected'
      ? { text: 'text-red-700 dark:text-red-400', bar: 'bg-red-500', track: 'bg-red-100 dark:bg-red-900/30', icon: 'text-red-500', label: run.status === 'rejected' ? 'Rejected' : 'Failed' }
    : { text: 'text-gray-600 dark:text-gray-400', bar: 'bg-gray-400', track: 'bg-gray-200 dark:bg-gray-700', icon: 'text-gray-400', label: run.status };

  const StatusIcon =
    run.status === 'completed' ? CheckCircle2 :
    run.status === 'failed' || run.status === 'rejected' ? CircleX :
    run.status === 'running' || run.status === 'pending' ? Loader2 :
    run.status === 'queued' ? Clock :
    Minus;

  const startDate = new Date(run.startedAt);
  const durationMs = run.finishedAt ? new Date(run.finishedAt).getTime() - startDate.getTime() : Date.now() - startDate.getTime();
  const durationStr = durationMs < 60000 ? `${Math.round(durationMs / 1000)}s` : `${Math.floor(durationMs / 60000)}m ${Math.round((durationMs % 60000) / 1000)}s`;
  const isRunning = run.status === 'running' || run.status === 'pending';
  const isQueued = run.status === 'queued';

  return (
    <div
      className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5 shadow-sm ${isRunning ? 'cursor-pointer hover:border-blue-300 dark:hover:border-blue-700 transition-colors' : ''} ${isQueued ? 'border-amber-200 dark:border-amber-800/50' : ''}`}
      onClick={isRunning && onClickRunning ? () => onClickRunning(run) : undefined}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusIcon className={`w-4 h-4 ${statusConfig.icon} ${isRunning ? 'animate-spin' : ''}`} />
            <span className={`text-sm font-semibold ${statusConfig.text}`}>{statusConfig.label}</span>
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <span className="text-sm text-gray-500">{startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {startDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
            {run.domain && (
              <>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{run.domain.toUpperCase()}</span>
              </>
            )}
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <span className="text-sm text-gray-500">{run.totalTalents} talent{run.totalTalents !== 1 ? 's' : ''}</span>
          </div>
          <div className="mt-1 flex items-center gap-2 flex-wrap text-sm text-gray-500">
            {run.adminEmail && (
              <span>Started by <span className="text-gray-700 dark:text-gray-300">{run.adminEmail}</span></span>
            )}
            {run.groupName && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-800">{run.groupName}</span>
            )}
          </div>
          {run.groupName && run.groupCounts && (
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); onNavigateToCandidates?.(run.groupName!, 'candidates'); }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Approved <span className="font-semibold">{run.groupCounts.accepted}</span>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onNavigateToCandidates?.(run.groupName!, 'in-review'); }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
              >
                In Review <span className="font-semibold">{run.groupCounts.inReview}</span>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onNavigateToCandidates?.(run.groupName!, 'rejected'); }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
              >
                Rejected <span className="font-semibold">{run.groupCounts.rejected}</span>
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {(isRunning || isQueued) && (
            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors disabled:opacity-50"
            >
              <Square className="w-3.5 h-3.5" />
              {cancelling ? 'Cancelling...' : 'Cancel'}
            </button>
          )}
          {run.hasInputData && (
            <button
              onClick={(e) => { e.stopPropagation(); downloadPipelineInput(run.id); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Input
            </button>
          )}
          {run.status === 'completed' && run.hasOutputData && (
            <button
              onClick={(e) => { e.stopPropagation(); downloadPipelineResult(run.id); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Output
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <div className={`relative h-1.5 flex-1 overflow-hidden rounded-full ${statusConfig.track}`}>
          <div
            className={`absolute inset-y-0 left-0 rounded-full transition-all ${statusConfig.bar}`}
            style={{ width: run.status === 'completed' || run.status === 'rejected' ? '100%' : isRunning ? '50%' : isQueued ? '0%' : `${run.status === 'failed' ? 70 : 0}%` }}
          />
        </div>
        {!isQueued && <span className="text-xs text-gray-500 tabular-nums shrink-0">{durationStr}</span>}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">{run.id}</span>
        {isRunning && <span className="text-xs text-blue-500 dark:text-blue-400 font-medium">Click to view progress</span>}
        {isQueued && <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Waiting for current run to finish</span>}
      </div>

      {(run.status === 'failed' || run.status === 'cancelled') && (
        <div className="mt-4 bg-red-50/50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-700 dark:text-red-300">
            {run.status === 'cancelled' ? 'This run was stopped.' : 'Something went wrong with this run. Please try again.'}
          </p>
        </div>
      )}
    </div>
  );
}

// Friendly status label + badge classes for a finished run.
function runStatusMeta(status: string): { label: string; cls: string } {
  switch (status) {
    case 'completed': return { label: 'Completed', cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' };
    case 'failed': return { label: 'Failed', cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' };
    case 'rejected': return { label: 'Rejected', cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' };
    case 'cancelled': return { label: 'Cancelled', cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' };
    default: return { label: status.charAt(0).toUpperCase() + status.slice(1), cls: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' };
  }
}

function fmtRunDateTime(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
}

function fmtDuration(startedAt: string, finishedAt: string | null): string {
  if (!finishedAt) return '—';
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 0) return '—';
  return ms < 60000 ? `${Math.round(ms / 1000)}s` : `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

const RUN_HISTORY_PAGE_SIZE = 15;

/** Filterable + sortable + paginated table of finished ingestion runs. All client-side —
 *  listPipelineRuns already returns the full set. */
function RunHistoryTable({ runs, onNavigateToCandidates }: {
  runs: PipelineRunSummary[];
  onNavigateToCandidates: (groupName?: string, tab?: CandidatesTab) => void;
}) {
  const [sort, setSort] = useState<{ col: string; dir: 'asc' | 'desc' }>({ col: 'startedAt', dir: 'desc' });
  const [filters, setFilters] = useState<{ status: string[]; domain: string[]; group: string[]; startedBy: string[]; source: string[] }>({ status: [], domain: [], group: [], startedBy: [], source: [] });
  const [page, setPage] = useState(1);

  const srcLabel = (r: PipelineRunSummary) => (r.fromSearchPortal ? 'Search 3.0' : 'CSV Upload');

  const opts = React.useMemo(() => {
    const uniq = (vals: (string | null | undefined)[]) => Array.from(new Set(vals.map((v) => v || '—'))).sort();
    return {
      status: uniq(runs.map((r) => runStatusMeta(r.status).label)),
      domain: uniq(runs.map((r) => (r.domain ? r.domain.toUpperCase() : '—'))),
      group: uniq(runs.map((r) => r.groupName)),
      startedBy: uniq(runs.map((r) => r.adminEmail)),
      source: uniq(runs.map(srcLabel)),
    };
  }, [runs]);

  const filtered = React.useMemo(() => {
    const match = (sel: string[], val: string) => sel.length === 0 || sel.includes(val);
    const rows = runs.filter((r) =>
      match(filters.status, runStatusMeta(r.status).label) &&
      match(filters.domain, r.domain ? r.domain.toUpperCase() : '—') &&
      match(filters.group, r.groupName || '—') &&
      match(filters.startedBy, r.adminEmail || '—') &&
      match(filters.source, srcLabel(r)),
    );
    const dir = sort.dir === 'asc' ? 1 : -1;
    const val = (r: PipelineRunSummary): string | number => {
      switch (sort.col) {
        case 'totalTalents': return r.totalTalents;
        case 'duration': return r.finishedAt ? new Date(r.finishedAt).getTime() - new Date(r.startedAt).getTime() : -1;
        case 'status': return runStatusMeta(r.status).label;
        case 'source': return srcLabel(r);
        case 'domain': return r.domain || '';
        case 'group': return r.groupName || '';
        case 'startedBy': return r.adminEmail || '';
        default: return new Date(r.startedAt).getTime();
      }
    };
    return [...rows].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [runs, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / RUN_HISTORY_PAGE_SIZE));
  const clampedPage = Math.min(page, totalPages);
  const paged = filtered.slice((clampedPage - 1) * RUN_HISTORY_PAGE_SIZE, clampedPage * RUN_HISTORY_PAGE_SIZE);

  const onSort = (col: string) => { setSort((s) => ({ col, dir: s.col === col && s.dir === 'desc' ? 'asc' : 'desc' })); setPage(1); };
  const onFilter = (key: keyof typeof filters, v: string[]) => { setFilters((f) => ({ ...f, [key]: v })); setPage(1); };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-600 dark:text-gray-400 min-w-[1000px]">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800">
            <tr>
              <SortFilterHeader label="Status" sortKey="status" currentSort={sort} onSort={onSort} filterOptions={opts.status} filterValue={filters.status} onFilter={(v) => onFilter('status', v)} />
              <SortFilterHeader label="Source" sortKey="source" currentSort={sort} onSort={onSort} filterOptions={opts.source} filterValue={filters.source} onFilter={(v) => onFilter('source', v)} />
              <SortFilterHeader label="Started" sortKey="startedAt" currentSort={sort} onSort={onSort} />
              <SortFilterHeader label="Domain" sortKey="domain" currentSort={sort} onSort={onSort} filterOptions={opts.domain} filterValue={filters.domain} onFilter={(v) => onFilter('domain', v)} />
              <SortFilterHeader label="Group" sortKey="group" currentSort={sort} onSort={onSort} filterOptions={opts.group} filterValue={filters.group} onFilter={(v) => onFilter('group', v)} />
              <SortFilterHeader label="Talents" sortKey="totalTalents" currentSort={sort} onSort={onSort} className="text-right" />
              <th scope="col" className="px-4 py-3 font-semibold whitespace-nowrap">Results</th>
              <SortFilterHeader label="Started by" sortKey="startedBy" currentSort={sort} onSort={onSort} filterOptions={opts.startedBy} filterValue={filters.startedBy} onFilter={(v) => onFilter('startedBy', v)} />
              <SortFilterHeader label="Duration" sortKey="duration" currentSort={sort} onSort={onSort} className="text-right" />
              <th scope="col" className="px-4 py-3 font-semibold text-right">Download</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {paged.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-400">No runs match the current filters.</td></tr>
            )}
            {paged.map((run) => {
              const meta = runStatusMeta(run.status);
              return (
                <tr key={run.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${meta.cls}`}>{meta.label}</span>
                    <div className="text-[10px] text-gray-400 dark:text-gray-600 font-mono mt-0.5 truncate max-w-[160px]" title={run.id}>{run.id}</div>
                  </td>
                  <td className="px-4 py-3">
                    {run.fromSearchPortal
                      ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-800">Search 3.0</span>
                      : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">CSV Upload</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtRunDateTime(run.startedAt)}</td>
                  <td className="px-4 py-3">{run.domain ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{run.domain.toUpperCase()}</span> : <span className="text-gray-400 text-xs">—</span>}</td>
                  <td className="px-4 py-3">{run.groupName ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-100 dark:border-blue-800 max-w-[200px] truncate">{run.groupName}</span> : <span className="text-gray-400 text-xs">—</span>}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{run.totalTalents}</td>
                  <td className="px-4 py-3">
                    {run.groupName && run.groupCounts ? (
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => onNavigateToCandidates(run.groupName!, 'candidates')} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors" title="Approved">✓ {run.groupCounts.accepted}</button>
                        <button onClick={() => onNavigateToCandidates(run.groupName!, 'in-review')} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors" title="In Review">◔ {run.groupCounts.inReview}</button>
                        <button onClick={() => onNavigateToCandidates(run.groupName!, 'rejected')} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors" title="Rejected">✗ {run.groupCounts.rejected}</button>
                      </div>
                    ) : <span className="text-gray-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{run.adminEmail || <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-500 tabular-nums whitespace-nowrap">{fmtDuration(run.startedAt, run.finishedAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      {run.hasInputData && (
                        <button onClick={() => downloadPipelineInput(run.id)} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" title="Download input CSV"><Download className="w-3.5 h-3.5" />In</button>
                      )}
                      {run.status === 'completed' && run.hasOutputData && (
                        <button onClick={() => downloadPipelineResult(run.id)} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" title="Download output"><Download className="w-3.5 h-3.5" />Out</button>
                      )}
                      {!run.hasInputData && !(run.status === 'completed' && run.hasOutputData) && <span className="text-gray-400 text-xs">—</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {filtered.length > RUN_HISTORY_PAGE_SIZE && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-800 text-sm text-gray-500 dark:text-gray-400">
          <span>{(clampedPage - 1) * RUN_HISTORY_PAGE_SIZE + 1}–{Math.min(clampedPage * RUN_HISTORY_PAGE_SIZE, filtered.length)} of {filtered.length}</span>
          <div className="flex items-center gap-1">
            <button disabled={clampedPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:pointer-events-none"><ChevronLeft className="w-4 h-4" /></button>
            <span className="px-2 tabular-nums">{clampedPage} / {totalPages}</span>
            <button disabled={clampedPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:pointer-events-none"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Data Ingestion Section ────────────────────────────────────────────────
function DataIngestionSection({ onNavigateToCandidates }: { onNavigateToCandidates: (groupName?: string, tab?: CandidatesTab) => void }) {
  const [showEnrichment, setShowEnrichment] = useState(false);
  const [runs, setRuns] = useState<PipelineRunSummary[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchRuns = useCallback(async () => {
    if (runs.length === 0) setRunsLoading(true);
    try {
      const data = await listPipelineRuns();
      setRuns(data);
    } catch { /* silent */ }
    finally { setRunsLoading(false); }
  }, [runs.length]);

  // Fetch on mount
  useEffect(() => { fetchRuns(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll every 5s while any run is in progress
  const hasRunning = runs.some((r) => r.status === 'running' || r.status === 'pending' || r.status === 'queued');
  useEffect(() => {
    if (hasRunning && !showEnrichment) {
      pollingRef.current = setInterval(fetchRuns, 5000);
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [hasRunning, showEnrichment, fetchRuns]);

  const [progressRun, setProgressRun] = React.useState<PipelineRunSummary | null>(null);

  if (showEnrichment) {
    return (
      <AddCandidatesPage
        onBack={() => { setShowEnrichment(false); fetchRuns(); }}
        onComplete={(groupName) => {
          setShowEnrichment(false);
          fetchRuns();
          onNavigateToCandidates(groupName);
        }}
      />
    );
  }

  const runningRuns = runs.filter((r) => r.status === 'running' || r.status === 'pending' || r.status === 'queued');
  const pastRuns = runs.filter((r) => r.status !== 'running' && r.status !== 'pending' && r.status !== 'queued');

  return (
    <div className="p-8 max-w-[1400px] mx-auto space-y-6 transition-opacity duration-300">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Data Ingestion</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Upload candidate CSVs and review past enrichment runs.
          </p>
        </div>
        <button
          onClick={() => setShowEnrichment(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Add Candidates
        </button>
      </div>

      {runsLoading && (
        <div className="p-12 text-center">
          <Loader2 className="w-6 h-6 text-blue-500 animate-spin mx-auto" />
        </div>
      )}

      {!runsLoading && runs.length === 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-16 text-center">
          <CloudUpload className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">No enrichment runs yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto mb-4">
            Upload a CSV and run the enrichment pipeline to get started.
          </p>
          <button
            onClick={() => setShowEnrichment(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Candidates
          </button>
        </div>
      )}

      {!runsLoading && runs.length > 0 && (
        <div className="space-y-6">
          {runningRuns.length > 0 && (
            <section className="space-y-3">
              <div className="flex items-center gap-2 px-1">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">In progress</h3>
                <span className="text-xs text-gray-500">({runningRuns.length})</span>
              </div>
              <div className="space-y-4">
                {runningRuns.map((run) => <RunCard key={run.id} run={run} onClickRunning={setProgressRun} onCancelled={fetchRuns} onNavigateToCandidates={onNavigateToCandidates} />)}
              </div>
            </section>
          )}

          {pastRuns.length > 0 && (
            <section className="space-y-3">
              <div className="flex items-center gap-2 px-1">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Run history</h3>
                <span className="text-xs text-gray-500">({pastRuns.length})</span>
              </div>
              <RunHistoryTable runs={pastRuns} onNavigateToCandidates={onNavigateToCandidates} />
            </section>
          )}
        </div>
      )}

      {progressRun && <RunProgressModal run={progressRun} onClose={() => setProgressRun(null)} />}
    </div>
  );
}

// Old CandidatesSection removed — now imported from ./admin/CandidatesSection.tsx

// ─── Main Page ─────────────────────────────────────────────────────────────
const AdminConsolePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSectionState] = useState<AdminSection>(() => {
    const slug = location.pathname.replace(/^\/admin\/?/, '').split('/')[0];
    return SLUG_TO_SECTION[slug] ?? 'candidates';
  });
  const [initialGroupFilter, setInitialGroupFilter] = useState<string | undefined>();
  const [initialTab, setInitialTab] = useState<CandidatesTab | undefined>();
  const { role } = useCurrentUser();

  useEffect(() => {
    const slug = location.pathname.replace(/^\/admin\/?/, '').split('/')[0];
    if (!slug || !SLUG_TO_SECTION[slug]) {
      navigate(`/admin/${SECTION_TO_SLUG[activeSection]}`, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const slug = location.pathname.replace(/^\/admin\/?/, '').split('/')[0];
    const section = SLUG_TO_SECTION[slug];
    if (section && section !== activeSection) {
      setActiveSectionState(section);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const setActiveSection = useCallback((section: AdminSection) => {
    setActiveSectionState(section);
    navigate(`/admin/${SECTION_TO_SLUG[section]}`);
  }, [navigate]);

  const handleSignOut = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('current_user');
    localStorage.removeItem('tid');
    localStorage.removeItem('own_tid');
    window.location.href = '/login';
  };

  return (
    <AdminDataProvider>
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex text-gray-900 dark:text-gray-100">
      {/* Sidebar */}
      <aside className="w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col fixed inset-y-0 left-0 z-10">
        <div className="h-16 flex items-center px-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-lg tracking-tight">Admin Console</span>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 overflow-y-auto scrollbar-thin">
          {SIDEBAR_GROUPS.map((group, gi) => (
            <div key={group.label} className={gi > 0 ? 'mt-6' : ''}>
              <div className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                {group.label}
              </div>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeSection === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                        isActive
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'
                      }`}
                    >
                      <Icon className={`w-5 h-5 shrink-0 ${isActive ? 'text-blue-700 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
                      {item.label}
                    </button>
                  );
                })}
                {group.label === 'Talent Model' && role === 'superadmin' && (
                  <button
                    type="button"
                    onClick={() => setActiveSection('prompt-lab')}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      activeSection === 'prompt-lab'
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    <FlaskConical className={`w-5 h-5 shrink-0 ${activeSection === 'prompt-lab' ? 'text-blue-700 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
                    Prompt Lab
                  </button>
                )}
                {group.label === 'Platform Administration' && (
                  <a
                    href="https://turing.grafana.net/d/su6zgtd/customer-portal?orgId=1&from=now-3h&to=now&timezone=browser&var-DS_PROMETHEUS=cemcxk86p35kwa"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200"
                  >
                    <ChartColumn className="w-5 h-5 shrink-0 text-gray-400 dark:text-gray-500" />
                    <span className="flex-1 text-left">Metrics Dashboard</span>
                    <ExternalLink className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 transition-colors group-hover:text-gray-500" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-200 dark:border-gray-800 space-y-1">
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            Customer Portal
          </button>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
          >
            <LogOut className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 min-h-screen min-w-0 overflow-x-hidden">
        {activeSection === 'ingestion' && <DataIngestionSection onNavigateToCandidates={(g, tab) => { setInitialGroupFilter(g); setInitialTab(tab); setActiveSection('candidates'); }} />}
        {activeSection === 'candidates' && <CandidatesSectionNew initialGroupFilter={initialGroupFilter} initialTab={initialTab} />}
        {activeSection === 'batches' && <BatchesSection />}
        {activeSection === 'access' && <AccessManagementSection />}
        {activeSection === 'configurator' && <SectionPlaceholder title={PAGE_META.configurator.title} description={PAGE_META.configurator.description} />}
        {activeSection === 'accounts' && <TenantsSection />}
        {activeSection === 'search-domains' && <SearchDomainsSection />}
        {activeSection === 'prompt-lab' && <PromptLabSection />}
        {activeSection === 'skills' && <SkillsSection />}
        {activeSection === 'link-health' && <LinkHealthSection />}
      </main>
    </div>
    </AdminDataProvider>
  );
};

export default AdminConsolePage;
